import { useFileDialog } from "@vueuse/core/index";
import { uploadFile } from "@/api/system/file";
import { match, P } from "ts-pattern";
import { isNil, noop } from "lodash-es";
import { ProdCons, sleep } from "@/utils/coroutine.js";
import { ServerResCodeTypeEnum } from "@/utils/apiHelper.js";
import { downloadTool } from "@/utils/tools.js";

export const FileUploadResMsgType = {
  StartAll: "StartAll", //  用户选择完成文件之后
  StartOne: "StartOne", //  用户选择完成文件之后
  ErrorOne: "ErrorOne", //  当服务端返回了一个上传的文件的结果失败后发送的消息
  CompleteOne: "CompleteOne", //  当服务端返回了一个上传的文件的结果成功后发送的消息
  EndAll: "EndAll" //  全部上传完成,尽管其中包含失败
};

class FileUploadSignal {
  constructor(type, data, error) {
    this.type = type;
    this.data = data;
    this.error = error;
  }

  static createCompleteMsg(data) {
    return new FileUploadSignal(FileUploadResMsgType.CompleteOne, data, null);
  }

  static createErrorMsg(e) {
    return new FileUploadSignal(FileUploadResMsgType.ErrorOne, null, e);
  }
}

export class GeneralUploadFileState {
  static UploadingState = {
    QueuedUpload: "QueuedUpload", //  initial state , wait for uploading
    Uploading: "Uploading",
    UploadSuccess: "UploadSuccess",
    UploadFailure: "UploadFailure"
  };

  constructor() {
    this.uploadState = GeneralUploadFileState.UploadingState.QueuedUpload;
    this.loadingProgress = 0;
    this.errInfo = null;
  }

  setState(newState) {
    this.uploadState = newState;
  }
}

/**
 *  A general img dto used in internal
 * @see "@\src\components\FileUpload\FileStateWrapper.vue"
 */
export class GeneralFileDTO {
  constructor() {
    this.$_GeneralUploadFileState = new GeneralUploadFileState();
  }

  setBenaGenFileDTO(benaGenFileDTO) {
    this.benaGenFileDTO = benaGenFileDTO;
    return this;
  }

  get uploadingState() {
    return this.$_GeneralUploadFileState.uploadState;
  }

  setUploading() {
    this.$_GeneralUploadFileState.setState(GeneralUploadFileState.UploadingState.Uploading);
    return this;
  }

  setSuccess(benaGenFileDTO) {
    this.setBenaGenFileDTO(benaGenFileDTO);
    this.$_GeneralUploadFileState.setState(GeneralUploadFileState.UploadingState.UploadSuccess);
    return this;
  }

  setLoadFailure(errInfo) {
    this.$_GeneralUploadFileState.errInfo = errInfo;
    this.$_GeneralUploadFileState.setState(GeneralUploadFileState.UploadingState.UploadFailure);
    return this;
  }

  onUploadingProgress(percentage) {
    this.$_GeneralUploadFileState.loadingProgress = percentage;
  }
}

export const FileTypeEnum = {
  IMAGE: "IMAGE",
  GENERAL: "GENERAL"
};

//  继发模式队列上传图片
export function useBenaGenUploadFile({ createUseFileDialog, fileType } = {}) {
  const { accept } = match(fileType)
    .with(FileTypeEnum.IMAGE, () => {
      return {
        accept: "image/*"
      };
    })
    .otherwise(() => {
      return {
        accept: "*/*"
      };
    });

  const { open, onChange, files } = match(createUseFileDialog)
    .with(P.nonNullable, createUseFileDialog)
    .otherwise(() => {
      return useFileDialog({
        accept,
        directory: false, // Select directories instead of files if set true
        multiple: true,
        reset: true
      });
    });

  let pc = new ProdCons();

  onChange(uploadFiles);

  async function uploadFiles(_files) {
    try {
      if (isNil(_files)) {
        return;
      }

      //  for the reason that this fn will be invoked multiple times
      //  each time an enclosure object will be created and pc will be captured at this callback fn
      //  save pointer which point to pc to prevent the problem of pc is overwritten
      let localPc = pc;

      const files = Array.from(_files);

      /**
       * @type {GeneralFileDTO[]}
       */
      const generalFileDTOList = files.map(() => reactive(new GeneralFileDTO()));

      localPc.send(new FileUploadSignal(FileUploadResMsgType.StartAll, generalFileDTOList));

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        //  开始阶段用户放入 placeholder
        const generalFileDTO = generalFileDTOList[i];
        localPc.send(new FileUploadSignal(FileUploadResMsgType.StartOne, generalFileDTO));

        generalFileDTO.setUploading();

        match(
          await uploadFile(file, {
            onProgress: generalFileDTO.onUploadingProgress.bind(generalFileDTO)
          }).catch((err) => err)
        )
          .with({ code: ServerResCodeTypeEnum.OK, data: P.select() }, (benaGenFileDTO) => {
            localPc.send(
              FileUploadSignal.createCompleteMsg(generalFileDTO.setSuccess(benaGenFileDTO))
            );
          })
          .otherwise((err) => {
            console.log(err);
            localPc.send(FileUploadSignal.createErrorMsg(generalFileDTO.setLoadFailure(err)));
          });
      }

      await sleep(0);

      localPc.send(new FileUploadSignal(FileUploadResMsgType.EndAll, generalFileDTOList));

      localPc.close();
    } catch (e) {
      console.log(e);
    }
  }

  function selectFilesAndUpload() {
    pc = new ProdCons();
    open();

    return pc.consumer();
  }

  return {
    selectFilesAndUpload,
    manualUploadFiles(files) {
      pc = new ProdCons();

      const c = pc.consumer();

      uploadFiles(files);

      return c;
    }
  };
}

const getFileDTO = (generalFile) =>
  match(generalFile)
    .with(P.instanceOf(GeneralFileDTO), () => generalFile.benaGenFileDTO)
    .otherwise(() => generalFile);

const IMAGE_TYPES = ["jpg", "jpeg", "png"];

export const GeneralFileUtils = {
  getFileId: (generalFile) => getFileDTO(generalFile).fileId,
  isImageType: (generalFile) => IMAGE_TYPES.includes(getFileDTO(generalFile).fileExt),
  downloadFile: (generalFile) => {
    const { showName, tempAccessUrl } = getFileDTO(generalFile);
    downloadTool(tempAccessUrl, showName);
  }
};

async function onUploadTest() {
  const { selectFilesAndUpload } = useBenaGenUploadFile();

  let saveCurrentCtx = "selectedElecParamEntry";

  for await (const signal of selectFilesAndUpload()) {
    match(signal)
      .with(
        {
          type: FileUploadResMsgType.StartAll,
          data: P.select()
        },
        (generalFileDTOList) => {}
      )
      .with(
        {
          type: FileUploadResMsgType.CompleteOne,
          data: P.select()
        },
        (generalFileDTO) => {}
      )
      .otherwise(noop); //  ignore other signals
  }
}
